/* eslint-disable @next/next/no-img-element */

import { useState, useEffect } from "preact/hooks";
import {
  usePurchasedProductDetail,
  type PurchasedProductDetail,
} from "@/hooks/usePurchasedProductDetail";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Minus, Plus, Loader2 } from "lucide-react";

interface WidgetBuyAgainSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: number | null;
}

function createCheckoutUrl(
  product: PurchasedProductDetail,
  selectedVariantId: number,
  quantity: number,
): string {
  const { customer, shop_domain } = product;
  const baseUrl = `https://${shop_domain}/cart/${selectedVariantId}:${quantity}`;

  const params = new URLSearchParams();

  if (customer.email) {
    params.append("checkout[email]", customer.email);
  }

  const { shipping_address } = customer;
  if (shipping_address) {
    if (shipping_address.first_name) {
      params.append(
        "checkout[shipping_address][first_name]",
        shipping_address.first_name,
      );
    }
    if (shipping_address.last_name) {
      params.append(
        "checkout[shipping_address][last_name]",
        shipping_address.last_name,
      );
    }
    if (shipping_address.address1) {
      params.append(
        "checkout[shipping_address][address1]",
        shipping_address.address1,
      );
    }
    if (shipping_address.address2) {
      params.append(
        "checkout[shipping_address][address2]",
        shipping_address.address2,
      );
    }
    if (shipping_address.city) {
      params.append("checkout[shipping_address][city]", shipping_address.city);
    }
    if (shipping_address.country) {
      params.append(
        "checkout[shipping_address][country]",
        shipping_address.country,
      );
    }
    if (shipping_address.province) {
      params.append(
        "checkout[shipping_address][province]",
        shipping_address.province,
      );
    }
    if (shipping_address.zip) {
      params.append("checkout[shipping_address][zip]", shipping_address.zip);
    }
    if (shipping_address.phone) {
      params.append(
        "checkout[shipping_address][phone]",
        shipping_address.phone,
      );
    }
  }

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

export function WidgetBuyAgainSheet({
  open,
  onOpenChange,
  productId,
}: WidgetBuyAgainSheetProps) {
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(
    null,
  );
  const [quantity, setQuantity] = useState(1);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" && window.innerWidth >= 768,
  );

  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    checkDesktop();
    window.addEventListener("resize", checkDesktop);
    return () => window.removeEventListener("resize", checkDesktop);
  }, []);

  const { data: product, isLoading } = usePurchasedProductDetail(
    productId,
    open,
  );

  const effectiveVariantId = selectedVariantId ?? product?.variant_id ?? null;

  const selectedVariant = product
    ? product.variants.find((v) => v.shopify_id === effectiveVariantId)
    : null;

  useEffect(() => {
    if (!open) {
      setSelectedVariantId(null);
      setQuantity(1);
    }
  }, [open]);

  const handleBuyNow = () => {
    if (!product || !effectiveVariantId) return;

    const checkoutUrl = createCheckoutUrl(
      product,
      effectiveVariantId,
      quantity,
    );
    window.location.href = checkoutUrl;
  };

  if (!productId) return null;

  const currentPrice = selectedVariant?.price ?? product?.price ?? 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isDesktop ? "right" : "bottom"}
        className="tw:h-auto tw:max-h-svh tw:overflow-y-auto tw:rounded-t-2xl md:tw:rounded-t-none md:tw:max-w-md md:tw:h-full md:tw:max-h-full"
        onOpenChange={onOpenChange}
      >
        <SheetHeader>
          <SheetTitle>Complete Your Purchase</SheetTitle>
        </SheetHeader>

        {isLoading || !product ? (
          <div className="tw:flex tw:items-center tw:justify-center tw:py-8">
            <Loader2 className="tw:size-6 tw:animate-spin tw:text-pink-500" />
          </div>
        ) : (
          <>
            <div className="tw:space-y-6 tw:px-4 tw:py-4">
              <div className="tw:flex tw:gap-4">
                <div className="tw:relative tw:w-20 tw:h-20 tw:bg-pink-50 tw:rounded-lg tw:shrink-0 tw:overflow-hidden">
                  <img
                    src={product.image_url}
                    alt={product.title}
                    className="tw:w-full tw:h-full tw:object-cover tw:rounded-lg"
                  />
                </div>
                <div className="tw:flex-1">
                  <h3 className="tw:font-medium tw:text-sm tw:line-clamp-2">
                    {product.title}
                  </h3>
                  <p className="tw:text-sm tw:text-gray-500 tw:mt-1">
                    ${currentPrice}
                  </p>
                </div>
              </div>

              {product.variants.length > 1 && (
                <div className="tw:flex tw:flex-col tw:gap-2">
                  <label className="tw:text-sm tw:font-medium">
                    {product.options.length === 1
                      ? product.options[0].name
                      : "Variant"}
                  </label>
                  <div className="tw:flex tw:flex-wrap tw:gap-2">
                    {product.variants.map((variant) => (
                      <Button
                        key={variant.shopify_id}
                        type="button"
                        variant={
                          effectiveVariantId === variant.shopify_id
                            ? "default"
                            : "outline"
                        }
                        size="sm"
                        onClick={() => setSelectedVariantId(variant.shopify_id)}
                        className="tw:h-auto tw:rounded-md tw:py-2 tw:px-3 tw:text-xs tw:whitespace-normal tw:flex-1 tw:min-w-[calc(50%-0.25rem)]"
                      >
                        {variant.title}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="tw:flex tw:flex-col tw:gap-2">
                <label className="tw:text-sm tw:font-medium">Quantity</label>
                <div className="tw:flex tw:items-center tw:gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="tw:rounded-md"
                    size="sm"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    <Minus className="tw:w-3 tw:h-3" />
                  </Button>
                  <span className="tw:w-8 tw:text-center tw:text-sm tw:font-medium">
                    {quantity}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    className="tw:rounded-md"
                    size="sm"
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    <Plus className="tw:w-3 tw:h-3" />
                  </Button>
                </div>
              </div>
            </div>

            <SheetFooter>
              <Button
                onClick={handleBuyNow}
                className="tw:w-full tw:rounded-lg tw:text-white tw:border-0 tw:font-medium"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(251, 187, 93, 1) 0%, rgba(252, 88, 153, 1) 100%)",
                  boxShadow: "rgba(251, 187, 93, 1) 0 0 0 0.5px",
                }}
              >
                Buy Now - ${(currentPrice * quantity).toFixed(2)}
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
